import { usePiecesStore } from '@/stores/pieces';
import { useSettingsStore } from '@/stores/settings';
import { useWorkspaceApi } from './useWorkspaceApi';
import { useFoldersStore } from '@/stores/folders';
import { parseStatementOfFactsSegments, resolveStatementPiece } from '@/utils/statementOfFacts';
import { PIECE_TAG, pieceIdFromTag } from '@/utils/pieceId';
import type { Piece, InsertedPiece } from '@/types';

const BORDEREAU_TAG = 'pappers-bordereau';

let lastPieceControlsFingerprint = '';

const getPieceControls = (contentControls: Word.ContentControl[]): Word.ContentControl[] => {
	return contentControls.filter((cc) => pieceIdFromTag(cc.tag) !== null);
};

const createPieceControlsFingerprint = (pieceControls: Word.ContentControl[]): string => {
	const occurrenceCounts = new Map<string, number>();
	for (const cc of pieceControls) {
		occurrenceCounts.set(cc.tag, (occurrenceCounts.get(cc.tag) ?? 0) + 1);
	}

	const seenOccurrences = new Map<string, number>();
	return JSON.stringify(
		pieceControls.map((cc) => {
			const occurrence = (seenOccurrences.get(cc.tag) ?? 0) + 1;
			seenOccurrences.set(cc.tag, occurrence);

			return {
				id: cc.id,
				tag: cc.tag,
				occurrence,
				occurrenceCount: occurrenceCounts.get(cc.tag) ?? 0
			};
		})
	);
};

export const useWordDocument = () => {
	const piecesStore = usePiecesStore();
	const settingsStore = useSettingsStore();
	const foldersStore = useFoldersStore();
	const { updateDocumentNumbers, syncExportStatus } = useWorkspaceApi();

	const findPieceForBordereau = (pieceId: string): Piece | undefined => {
		return piecesStore.allPieces.find((p) => p.id === pieceId) ?? piecesStore.pieces.find((p) => p.id === pieceId);
	};

	const insertPiece = async (piece: Piece): Promise<void> => {
		await Word.run(async (context) => {
			const selection = context.document.getSelection();
			const parentCC = selection.parentContentControlOrNullObject;
			parentCC.load('isNullObject');
			await context.sync();

			// Si le curseur est dans un content control verrouillé, on se place juste après
			let range = selection;
			if (!parentCC.isNullObject) {
				range = parentCC.getRange(Word.RangeLocation.after);
			}

			range = range.insertText('\n', Word.InsertLocation.after);
			const insertedRange = range.insertText(' ', Word.InsertLocation.after);
			const contentControl = insertedRange.insertContentControl();
			contentControl.tag = `${PIECE_TAG}:${piece.id}`;
			contentControl.title = (piece.name || '').slice(0, 64).replace(/[^\w\s\-().,'àâéèêëïîôùûüç]/gi, '');
			contentControl.appearance = Word.ContentControlAppearance.boundingBox;
			contentControl.cannotDelete = false;
			contentControl.insertText('...', Word.InsertLocation.replace);
			contentControl.cannotEdit = true;
			contentControl.getRange(Word.RangeLocation.after).insertText('\n', Word.InsertLocation.after);

			await context.sync();
		});

		await renumber();
	};

	const renumber = async (): Promise<void> => {
		await Word.run(async (context) => {
			const contentControls = context.document.contentControls;
			contentControls.load('items/tag,items/id');
			await context.sync();

			// Filtrer les content controls de pièces
			const pieceControls = getPieceControls(contentControls.items);
			const currentFingerprint = createPieceControlsFingerprint(pieceControls);

			// Collecter les pièces insérées dans l'ordre d'apparition (1ère occurrence)
			const seen = new Map<string, { controlIds: number[]; number: number }>();
			const orderedPieceIds: string[] = [];

			for (const cc of pieceControls) {
				const pieceId = pieceIdFromTag(cc.tag)!;
				const entry = seen.get(pieceId);
				if (entry) {
					entry.controlIds.push(cc.id);
				} else {
					seen.set(pieceId, { controlIds: [cc.id], number: 0 }); // numéro provisoire
					orderedPieceIds.push(pieceId);
				}
			}

			// Collecter les numéros réservés par les pièces Pappers clientes (y compris celles non insérées dans Word)
			// allPieces contient toutes les pièces sans filtre ; pieces contient les pièces affichées
			const allPieces = piecesStore.allPieces.length > 0 ? piecesStore.allPieces : piecesStore.pieces;
			const reservedNumbers = new Set<number>();
			for (const piece of allPieces) {
				if (piece.number && piece.tags === 'Client') reservedNumbers.add(piece.number);
			}

			// Attribuer les numéros : priorité au numéro Pappers (si client), sinon auto-incrémentation
			const usedNumbers = new Set<number>();
			let autoNumber = 1;
			for (const pieceId of orderedPieceIds) {
				const piece = allPieces.find((p) => p.id === pieceId);
				if (piece?.number && piece.tags === 'Client' && !usedNumbers.has(piece.number)) {
					seen.get(pieceId)!.number = piece.number;
					usedNumbers.add(piece.number);
				} else {
					while (reservedNumbers.has(autoNumber) || usedNumbers.has(autoNumber)) {
						autoNumber++;
					}
					seen.get(pieceId)!.number = autoNumber;
					usedNumbers.add(autoNumber);
					autoNumber++;
				}
			}

			// Mettre à jour le texte et le style de chaque content control
			const paragraphFormatTargets: { control: Word.ContentControl; textRange: Word.Range; paragraph: Word.Paragraph }[] =
				[];
			for (const cc of pieceControls) {
				const pieceId = pieceIdFromTag(cc.tag)!;
				const info = seen.get(pieceId)!;
				const piece = allPieces.find((p) => p.id === pieceId) || piecesStore.allPieces.find((p) => p.id === pieceId);
				if (!piece) continue;

				const text = settingsStore.formatPiece(info.number, piece.name, piece.date);
				cc.cannotEdit = false;
				const textRange = cc.insertText(text, 'Replace');
				textRange.font.highlightColor = 'LightGray';
				const paragraph = textRange.paragraphs.getFirst();
				textRange.load('text');
				paragraph.load('text');
				paragraphFormatTargets.push({ control: cc, textRange, paragraph });
			}

			await context.sync();

			for (const { control, textRange, paragraph } of paragraphFormatTargets) {
				const controlText = textRange.text.trim();
				if (controlText && paragraph.text.trim() === controlText) {
					paragraph.alignment = 'Right';
				}
				control.cannotEdit = true;
			}

			await context.sync();

			const previouslyInsertedPieceIds = new Set(piecesStore.insertedPieces.map((ip) => ip.pieceId));

			// Mettre à jour le store local
			const insertedPieces: InsertedPiece[] = [];
			for (const [pieceId, info] of seen) {
				insertedPieces.push({
					pieceId,
					contentControlIds: info.controlIds,
					number: info.number
				});
			}
			piecesStore.setInsertedPieces(insertedPieces);
			lastPieceControlsFingerprint = currentFingerprint;

			// Mettre à jour les bordereaux
			await updateBordereaux(context, insertedPieces);
			await context.sync();

			// Sync avec l'API
			await syncNumbers(insertedPieces, previouslyInsertedPieceIds);
		});
	};

	const updateBordereaux = async (context: Word.RequestContext, insertedPieces: InsertedPiece[]): Promise<void> => {
		const contentControls = context.document.contentControls;
		contentControls.load('items/tag');
		await context.sync();

		const bordereaux = contentControls.items.filter((cc) => cc.tag === BORDEREAU_TAG);
		if (bordereaux.length === 0) return;

		const lines = insertedPieces
			.map((ip) => {
				const piece = findPieceForBordereau(ip.pieceId);
				if (!piece) return '';
				return settingsStore.formatPiece(ip.number, piece.name, piece.date);
			})
			.filter(Boolean);

		const bordereauText = lines.join('\n');

		for (const bordereau of bordereaux) {
			bordereau.cannotEdit = false;
			bordereau.insertText(bordereauText, 'Replace');
			bordereau.cannotEdit = true;
		}
	};

	const insertBordereau = async (): Promise<void> => {
		await Word.run(async (context) => {
			const range = context.document.getSelection();
			const contentControl = range.insertContentControl();
			contentControl.tag = BORDEREAU_TAG;
			contentControl.title = 'Bordereau de pièces';
			contentControl.appearance = Word.ContentControlAppearance.boundingBox;
			contentControl.cannotDelete = false;

			const lines = piecesStore.insertedPieces
				.map((ip) => {
					const piece = findPieceForBordereau(ip.pieceId);
					if (!piece) return '';
					return settingsStore.formatPiece(ip.number, piece.name, piece.date);
				})
				.filter(Boolean);

			contentControl.insertText(lines.join('\n') || 'Aucune pièce insérée', 'Replace');
			contentControl.cannotEdit = true;
			await context.sync();
		});
	};

	const scrollToPiece = async (pieceId: string): Promise<void> => {
		await Word.run(async (context) => {
			const contentControls = context.document.contentControls;
			contentControls.load('items/tag');
			await context.sync();

			const target = contentControls.items.find((cc) => cc.tag === `${PIECE_TAG}:${pieceId}`);
			if (target) {
				target.select();
				await context.sync();
			}
		});
	};

	const removePiece = async (pieceId: string): Promise<void> => {
		await Word.run(async (context) => {
			const contentControls = context.document.contentControls;
			contentControls.load('items/tag');
			await context.sync();

			const targets = contentControls.items.filter((cc) => cc.tag === `${PIECE_TAG}:${pieceId}`);
			for (const cc of targets) {
				cc.delete(false);
			}
			await context.sync();
			await renumber();
		});
	};

	const syncNumbers = async (insertedPieces: InsertedPiece[], previouslyInsertedPieceIds: Set<string>): Promise<void> => {
		const folder = foldersStore.selectedFolder;
		if (!folder) return;

		const updates: Record<string, number | null> = {};
		for (const ip of insertedPieces) {
			updates[ip.pieceId] = ip.number;
			previouslyInsertedPieceIds.delete(ip.pieceId);
		}
		for (const pieceId of previouslyInsertedPieceIds) {
			updates[pieceId] = null;
		}

		if (Object.keys(updates).length) {
			await updateDocumentNumbers(folder.id, updates);
		}

		const insertedIds = insertedPieces.map((ip) => ip.pieceId);
		if (insertedIds.length) {
			await syncExportStatus(folder.id, insertedIds);
		}

		const notInsertedIds = piecesStore.allPieces.filter((p) => !insertedIds.includes(p.id)).map((p) => p.id);
		if (notInsertedIds.length) {
			await syncExportStatus(folder.id, notInsertedIds, false);
		}
	};

	const detectDeletedControls = async (): Promise<void> => {
		// Appelé périodiquement ou via événement pour détecter les suppressions manuelles
		let hasChanges = false;
		await Word.run(async (context) => {
			const contentControls = context.document.contentControls;
			contentControls.load('items/tag,items/id');
			await context.sync();

			const currentFingerprint = createPieceControlsFingerprint(getPieceControls(contentControls.items));
			const hasPreviousSnapshot = lastPieceControlsFingerprint !== '';
			hasChanges = hasPreviousSnapshot
				? currentFingerprint !== lastPieceControlsFingerprint
				: piecesStore.insertedPieces.length > 0 || currentFingerprint !== createPieceControlsFingerprint([]);
		});

		if (hasChanges) {
			await renumber();
		}
	};

	const insertStatementOfFacts = async (text: string): Promise<void> => {
		const segments = parseStatementOfFactsSegments(text);

		await Word.run(async (context) => {
			const selection = context.document.getSelection();
			const parentCC = selection.parentContentControlOrNullObject;
			parentCC.load('isNullObject');
			await context.sync();

			// Si le curseur est dans un content control verrouillé, on se place après
			let currentRange = !parentCC.isNullObject ? parentCC.getRange(Word.RangeLocation.after) : selection;

			// Insérer les segments dans l'ordre
			for (const segment of segments) {
				if (segment.type === 'text') {
					currentRange = currentRange.insertText(segment.content, 'After');
				} else {
					const resolvedPiece = resolveStatementPiece(
						segment,
						piecesStore.allPieces,
						piecesStore.pieces,
						piecesStore.insertedPieces
					);

					if (resolvedPiece) {
						// Saut de ligne avant pour isoler la pièce sur son propre paragraphe
						currentRange = currentRange.insertText('\n', 'After');
						const placeholder = currentRange.insertText(' ', 'After');
						const contentControl = placeholder.insertContentControl();
						contentControl.tag = `${PIECE_TAG}:${resolvedPiece.id}`;
						contentControl.title = (resolvedPiece.title || '')
							.slice(0, 64)
							.replace(/[^\w\s\-().,'àâéèêëïîôùûüç]/gi, '');
						contentControl.appearance = Word.ContentControlAppearance.boundingBox;
						contentControl.cannotDelete = false;
						const insertedText = contentControl.insertText(segment.content, 'Replace');
						insertedText.font.highlightColor = 'LightGray';
						contentControl.cannotEdit = true;
						currentRange = contentControl.getRange('After');
						// Saut de ligne après pour que le texte suivant reste à gauche
						currentRange = currentRange.insertText('\n', 'After');
					} else {
						// Pièce non trouvée dans le dossier → insérer en texte brut
						currentRange = currentRange.insertText(segment.content, 'After');
					}
				}
			}

			await context.sync();
		});

		await renumber();
	};

	const getDocumentFolderId = async (): Promise<string | null> => {
		let folderId: string | null = null;
		await Word.run(async (context) => {
			const props = context.document.properties.customProperties;
			props.load('items');
			await context.sync();
			const prop = props.items.find((p) => p.key === 'pappersFolderId');
			if (prop) folderId = prop.value;
		});
		return folderId;
	};

	const setDocumentFolderId = async (folderId: string): Promise<void> => {
		await Word.run(async (context) => {
			context.document.properties.customProperties.add('pappersFolderId', folderId);
			await context.sync();
		});
	};

	return {
		insertPiece,
		insertBordereau,
		insertStatementOfFacts,
		scrollToPiece,
		removePiece,
		renumber,
		detectDeletedControls,
		getDocumentFolderId,
		setDocumentFolderId
	};
};
